import { Fragment, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import * as Sentry from '@sentry/react'

import usePageModal from 'features/Modal/hooks/usePageModal'
import { logEvent } from 'lib/analytics'
import { isIOS, isMobileDevice } from 'lib/device'
import { ROUTE_PATH, EXTERNAL_PATH } from 'lib/paths'
import { LocalStorage, SessionStorage } from 'lib/storage'
import { useResponsive } from 'hooks'
import { loginWithEmail, selectLoginWithEmail } from 'store/ducks/auth'
import { Link, useNavigate } from 'features/i18n/router'
import { MODAL_MODE } from '../config'
import { selectIsLoggedIn } from 'store/selectors'
import { useSnsSignInMutation } from 'store/api/yay/user'
import { signInResUserExistsCallback } from 'features/SNS/useSNS'
import { SNSSignInError } from 'lib/_types'

import Button from 'components/Button'
import { PasswordInput } from 'components/Form'
import { LoginMethod } from './LoginMethod'
import { CodeInput } from 'components'

import { CODE_LENGTH } from '../../../features/UserSetting/TwoFaSetting/TwoFaSetting'
import { useBannerRemoteConfig } from 'features/RemoteConfig/useBannerRemoteConfig'

import './style.scss'
import { ExternalLink } from 'components/ExternalLink'

const LoginModal = ({ location, redirectUrl }) => {
  const banner = useBannerRemoteConfig()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { generatePageModalUrl } = usePageModal()
  const { isMobile } = useResponsive()

  const isFacebookLoginDisabled = banner?.data?.is_facebook_login_broken

  const [snsSignIn, snsSignInRes] = useSnsSignInMutation()

  const isLoggedIn = useSelector(selectIsLoggedIn)

  const hideDownloadAppAlert = LocalStorage.hideDownloadAppAlert.get(false)

  const ___$email = useRef<HTMLInputElement>()

  const [__email, __emailSet] = useState('')
  const [__password, __passwordSet] = useState('')
  const [__code, __codeSet] = useState('')
  const [__error, __errorSet] = useState('')

  const [__doNotShow, __doNotShowSet] = useState(hideDownloadAppAlert)
  const [__showLoginForm, __showLoginFormSet] = useState(
    hideDownloadAppAlert || !isMobileDevice
  )

  const loginWithEmailState = useSelector(selectLoginWithEmail)

  useEffect(() => {
    window.setTimeout(() => {
      ___$email.current?.focus?.()
    }, 400)
  }, [])

  const error_code =
    loginWithEmailState?.error?.error_code ??
    (snsSignInRes?.error as any)?.data?.res?.error_code

  useEffect(() => {
    if (
      error_code == -32 &&
      !loginWithEmailState?.isLoading &&
      !snsSignInRes?.isLoading
    ) {
      __errorSet(t('user_setting.2fa.code.error.incorrect'))
    }
  }, [error_code, loginWithEmailState, snsSignInRes])

  useEffect(() => {
    if (!isLoggedIn) return

    if (window.opener && SessionStorage.authorizeWalletParams.get()) {
      navigate(ROUTE_PATH.AUTHORIZE_WALLET)
      return
    }

    navigate(ROUTE_PATH.HOME)
  }, [isLoggedIn])

  if (isLoggedIn) {
    return null
  }

  const shouldShowCodeInput =
    [-31, -32].includes(error_code) ||
    searchParams.get('snsAuthorizationResponse')

  return (
    <Fragment>
      <Link
        className="Modal__overlay"
        to={location.pathname}
        replace
        onClick={(e) => {
          if (shouldShowCodeInput) {
            e.preventDefault()

            // Need to reset the login state, letting a user,
            // input username/password, or do SNS sign in, again.
            window.location.href = location.pathname
          }
        }}
      />

      <div className="Modal__wrapper Modal__wrapper--auto-h LoginModal">
        {isMobile && (
          <div className="Modal__header">
            <a
              className="Modal__header__close"
              onClick={() => {
                if (shouldShowCodeInput) {
                  window.location.href = location.pathname
                } else {
                  navigate(location.pathname)
                }
              }}
            ></a>

            <Link to={ROUTE_PATH.HOME}>
              <img
                className="Header__yay_logo"
                src="/images/logotype.svg"
                alt="yay! logo"
              />
            </Link>
          </div>
        )}

        <div className="Modal__body">
          {__showLoginForm ? (
            <Fragment>
              {shouldShowCodeInput ? (
                <div className="LoginModal__panel__wrapper">
                  <h3 className="LoginModal__panel__h3">
                    {t('login_form.2fa.h3')}
                  </h3>

                  <div className="LoginModal__panel__2fa">
                    <p className="LoginModal__panel__2fa__p">
                      {t('login_form.2fa.p')}
                    </p>

                    <form
                      className="LoginModal__panel__2fa__form"
                      autoComplete="off"
                      onSubmit={(e) => {
                        e.preventDefault()

                        if (searchParams.get('snsAuthorizationResponse')) {
                          const snsAuthorizationResponse: {
                            data: SNSSignInError
                          } = JSON.parse(
                            searchParams.get('snsAuthorizationResponse')
                          )

                          snsSignIn({
                            ...snsAuthorizationResponse?.data?.req,
                            two_fa_code: __code,
                          })
                            .unwrap()
                            .then((signInRes) => {
                              Sentry.setUser({
                                id: signInRes?.res?.user_id,
                                username: signInRes?.res?.username,
                              })
                              signInResUserExistsCallback({ signInRes })
                            })
                            .catch(() => {
                              // Don't care.
                            })
                        } else {
                          dispatch(
                            loginWithEmail({
                              email: __email,
                              password: __password,
                              redirectUrl,
                              two_fa_code: __code,
                            })
                          )
                        }
                      }}
                    >
                      <div className="LoginModal__panel__2fa__form__code">
                        <CodeInput
                          value={__code}
                          setValue={__codeSet}
                          length={CODE_LENGTH}
                          error={__error}
                          clearError={() => __errorSet('')}
                        />
                      </div>

                      <p className="LoginModal__panel__2fa__p">
                        <a
                          className="UserSetting__code-verification__a UserSetting__code-verification__a--external"
                          href={EXTERNAL_PATH.TWO_FA()}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                        >
                          {t('user_setting.2fa.code.a')}
                        </a>
                      </p>

                      <div className="LoginModal__panel__2fa__form__buttons">
                        <Button
                          type="submit"
                          size="large"
                          disabled={__code?.length < CODE_LENGTH}
                          loading={
                            loginWithEmailState.isLoading ||
                            snsSignInRes?.isLoading
                          }
                        >
                          {t('login_form.2fa.submit')}
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>
              ) : (
                <div className="LoginModal__panel__wrapper">
                  <h3 className="LoginModal__panel__h3">{t('common.login')}</h3>

                  <form
                    className="LoginModal__panel__form"
                    onSubmit={(e) => {
                      e.preventDefault()

                      logEvent.account_log_in({
                        type: 'email',
                        from: 'first_view',
                      })

                      dispatch(
                        loginWithEmail({
                          email: __email,
                          password: __password,
                          redirectUrl,
                        })
                      )
                    }}
                  >
                    <input
                      type="text"
                      className="Input"
                      name="email"
                      placeholder={t('common.email_address')}
                      ref={___$email}
                      value={__email}
                      onChange={(e) => {
                        __emailSet(e?.target?.value)
                      }}
                    />

                    <PasswordInput
                      name="password"
                      placeholder={t('login_form.password.placeholder')}
                      value={__password}
                      onChange={(e) => {
                        __passwordSet(e?.target?.value)
                      }}
                    />

                    <Link
                      className="LoginModal__panel__form__a"
                      to={ROUTE_PATH.RESET_PASSWORD}
                    >
                      {t('common.incase_forgot_password')}
                    </Link>

                    <div className="LoginModal__panel__form__buttons">
                      <Button
                        icon="login"
                        loading={loginWithEmailState.isLoading}
                        type="submit"
                        size="large"
                      >
                        {t('login_form.submit_button')}
                      </Button>
                    </div>
                  </form>

                  <div className="LoginModal__panel__hr">
                    {t('login_form.account_login_another_way')}
                  </div>

                  <LoginMethod />

                  {isFacebookLoginDisabled && (
                    <div
                      className="LoginMethods__footer"
                      dangerouslySetInnerHTML={{
                        __html: t('login_form.facebook_temporarily_disabled'),
                      }}
                    />
                  )}

                  <div className="LoginModal__panel__hr--full" />

                  <div className="LoginModal__panel__signup">
                    <Trans i18nKey="login_form.go_to_sign_up">
                      <Link
                        className="LoginModal__panel__signup__a"
                        to={generatePageModalUrl(
                          MODAL_MODE.SIGN_UP,
                          {},
                          window.location.pathname
                        )}
                      />
                    </Trans>
                  </div>
                </div>
              )}
            </Fragment>
          ) : (
            <div className="LoginModal__panel__wrapper">
              <div className="LoginModal__panel__promote-app">
                <div className="LoginModal__panel__promote-app__app">
                  <div className="LoginModal__panel__promote-app__app__yay-logo" />

                  <div>
                    <div className="LoginModal__panel__promote-app__app__title">
                      {t('common.suggest_yay_application')}
                    </div>

                    <ExternalLink
                      href={
                        isIOS
                          ? EXTERNAL_PATH.DOWNLOAD_LINK_IOS
                          : EXTERNAL_PATH.DOWNLOAD_LINK_ANDROID
                      }
                    >
                      <Button>{t('common.open_application')}</Button>
                    </ExternalLink>
                  </div>
                </div>

                <div className="LoginModal__panel__promote-app__description">
                  {t('common.call_function_promote')}
                </div>

                <div className="LoginModal__panel__promote-app__footer">
                  <div className="LoginModal__panel__promote-app__footer__doNotShow">
                    <input
                      id="doNotShow"
                      type="checkbox"
                      onChange={() => {
                        LocalStorage.hideDownloadAppAlert.set(!__doNotShow)
                        __doNotShowSet(!__doNotShow)
                      }}
                      checked={__doNotShow}
                    />
                    <label htmlFor="doNotShow">
                      {t('common.do_not_show_again')}
                    </label>
                  </div>

                  <a
                    onClick={() => {
                      __showLoginFormSet(true)
                    }}
                  >
                    {t('common.continue_on_web')}
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Fragment>
  )
}

export default LoginModal
